import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Scissors } from "lucide-react";
import { useStore } from "@/context/Store";
import { http, img, inr } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Butterflies } from "@/components/Butterflies";
import { Hero } from "@/components/home/Hero";
import { Gallery } from "@/components/home/Gallery";
import { SpatialStudio } from "@/components/home/SpatialStudio";
import { Reels } from "@/components/home/Reels";
import { Reviews } from "@/components/Reviews";
import { StyledByYou } from "@/components/home/StyledByYou";
import { InstaFeed } from "@/components/home/InstaFeed";
import { Reveal } from "@/lib/useInView";

const Row = ({ title, eyebrow, items, to, testid, note }) => (
  <section className="max-w-[1500px] mx-auto px-4 lg:px-10 py-20" data-testid={testid}>
    <div className="flex items-end justify-between mb-10"><div><p className="eyebrow text-[var(--rose-deep)]">{eyebrow}</p><h2 className="serif text-4xl lg:text-5xl mt-2">{title}</h2>{note && <p className="text-xs text-[var(--taupe)] mt-2">{note}</p>}</div><Link to={to} className="arrow-link eyebrow text-[0.62rem] flex items-center gap-2" data-testid={`${testid}-all`}>View all <ArrowRight className="w-4 h-4" /></Link></div>
    <div className="flex lg:grid lg:grid-cols-4 gap-4 lg:gap-7 overflow-x-auto no-scrollbar snap-x snap-mandatory -mx-4 px-4 lg:mx-0 lg:px-0">{items.slice(0, 4).map((p, k) => <Reveal key={p.id} delay={k * 90} className="snap-start shrink-0 w-[68%] sm:w-[42%] lg:w-auto"><ProductCard p={p} /></Reveal>)}</div>
  </section>
);

const Teasers = () => (
  <section className="max-w-[1500px] mx-auto px-4 lg:px-10 pt-16 lg:pt-20 grid lg:grid-cols-2 gap-5" data-testid="teasers">
    <Link to="/try-on" className="group relative overflow-hidden bg-[var(--blush)] border border-[var(--rose-soft)] p-10 lg:p-14 min-h-[280px] flex flex-col justify-between" data-testid="tryon-teaser">
      <p className="eyebrow flex items-center gap-2 text-[var(--rose-deep)]"><Sparkles className="w-4 h-4" /> AI virtual try-on</p>
      <div><h2 className="serif text-4xl lg:text-5xl">See it on you.</h2><p className="mt-3 text-[#5b4d4a] max-w-md">Upload a photo and see yourself in any piece, in every way to wear it. Private, deleted right after.</p></div>
      <ArrowRight className="absolute right-10 bottom-10 w-6 h-6 transition-transform group-hover:translate-x-2" />
    </Link>
    <Link to="/custom" className="group relative overflow-hidden bg-[var(--ox)] text-[var(--ivory)] p-10 lg:p-14 min-h-[280px] flex flex-col justify-between" data-testid="custom-teaser">
      <p className="eyebrow flex items-center gap-2 text-[var(--rose-soft)]"><Scissors className="w-4 h-4" /> Custom orders</p>
      <div><h2 className="serif text-4xl lg:text-5xl">Made just for you.</h2><p className="mt-3 text-white/75 max-w-md">A different style, your own print, a perfect fit. Share your idea and we'll send you a quote.</p></div>
      <ArrowRight className="absolute right-10 bottom-10 w-6 h-6 transition-transform group-hover:translate-x-2" />
    </Link>
  </section>
);

const bestPiece = (list) => [...list].sort((a, b) => b.looks.filter((l) => l.image || l.video).length - a.looks.filter((l) => l.image || l.video).length)[0];

export default function Home() {
  const { settings: s } = useStore();
  const [all, setAll] = useState([]), [best, setBest] = useState({ items: [] });
  useEffect(() => {
    http.get("/products", { params: { limit: 48 } }).then((r) => setAll(r.data.items));
    http.get("/products", { params: { tag: "bestsellers" } }).then((r) => setBest(r.data));
  }, []);
  if (!s) return <div className="h-[70vh]" />;
  const conv = all.filter((p) => p.convertible && p.looks.length > 1);
  const piece = bestPiece(conv);
  return (
    <div data-testid="home">
      <Butterflies />
      <Hero s={s} piece={piece} />
      <section className="max-w-[1500px] mx-auto px-4 lg:px-10 py-16" data-testid="moods"><p className="eyebrow text-[var(--rose-deep)]">Shop by mood</p>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border-t border-l border-[var(--stone)]">{s.moods.map((m, k) => <Link key={m} to={`/shop?mood=${encodeURIComponent(m)}`} className="border-r border-b border-[var(--stone)] p-6 h-36 flex flex-col justify-between hover:bg-[var(--ox)] hover:text-white transition-colors duration-500" data-testid={`mood-${k}`}><span className="eyebrow text-[0.55rem] text-[var(--rose)]">0{k + 1}</span><span className="serif text-2xl">{m}</span></Link>)}</div></section>
      <SpatialStudio items={all} />
      <Teasers />
      <Reels items={all} />
      <Gallery items={all} />
      <Reviews />
      <StyledByYou />
      {all.length >= 12 && <Row title="Bestsellers" eyebrow={best.bestseller_mode === "sales" ? "Most loved" : "Curated by the studio"} items={best.items} to="/shop?tag=bestsellers" testid="bestsellers" note={best.bestseller_mode === "sales" ? "" : "Hand-picked until we have enough sales to rank"} />}
      <InstaFeed />
      <section className="max-w-[1100px] mx-auto px-4 lg:px-10 pb-24" data-testid="community-story">
        <div><p className="eyebrow text-[var(--rose-deep)]">Our philosophy</p><p className="serif text-3xl leading-snug mt-3">Rewear it. Restyle it. Reinvent it.</p><p className="text-[#5b4d4a] mt-3 max-w-2xl">Every REVASTRA piece is designed to be worn in more ways than one, so one outfit carries you from campus to dinner to the weekend.</p><Link to="/story" className="eyebrow text-[0.62rem] underline mt-5 inline-block" data-testid="read-story">Read our story</Link></div>
      </section>
    </div>
  );
}
